import { useEffect, useState } from "react";
import { Star, Trash2, Loader2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n/i18n";

export async function toggleFavorite(pid, value, t) {
  try {
    await api.put(`/company/candidates/${pid}/favorite`, { favorite: value });
    toast.success(t(value ? "Marcado como favorito" : "Quitado de favoritos"));
    return true;
  } catch (e) { toast.error(t(apiError(e).message)); return false; }
}

export const FavoriteButton = ({ pid, value, onChange, testId, className = "" }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const click = async (e) => {
    e.stopPropagation();
    setBusy(true);
    if (await toggleFavorite(pid, !value, t)) onChange(!value);
    setBusy(false);
  };
  return (
    <button onClick={click} disabled={busy} title={t(value ? "Quitar de favoritos" : "Marcar como favorito")} aria-pressed={value} className={`flex items-center justify-center transition-colors disabled:opacity-50 ${className}`} data-testid={testId}>
      <Star className={`h-4 w-4 ${value ? "fill-amber-400 text-amber-500" : "text-slate-300 hover:text-amber-500"}`} />
    </button>
  );
};

const NoteItem = ({ n, onDelete }) => {
  const { t, lang } = useI18n();
  return (
    <li className="rounded-2xl border border-slate-200 p-4" data-testid={`candidate-note-${n.id}`}>
      <p className="whitespace-pre-line text-sm text-slate-800">{n.text}</p>
      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
        <span>{n.userName} · {new Date(n.createdAt).toLocaleString(lang, { dateStyle: "short", timeStyle: "short" })}</span>
        {n.canDelete && <button onClick={() => onDelete(n.id)} title={t("Borrar nota")} className="rounded p-1 hover:bg-slate-100 hover:text-upv" data-testid={`candidate-note-delete-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </li>
  );
};

export const CandidateNotes = ({ pid, onCount }) => {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setItems(null); api.get(`/company/candidates/${pid}/notes`).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [pid]);
  const update = (xs) => { setItems(xs); onCount(xs.length); };
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/company/candidates/${pid}/notes`, { text }); update([data, ...items]); setText(""); } catch (e) { toast.error(t(apiError(e).message)); } finally { setBusy(false); }
  };
  const remove = async (nid) => {
    try { await api.delete(`/company/candidates/${pid}/notes/${nid}`); update(items.filter((n) => n.id !== nid)); } catch (e) { toast.error(t(apiError(e).message)); }
  };
  return (
    <div data-testid="candidate-notes">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400"><StickyNote className="h-3.5 w-3.5" /> {t("Notas del equipo")}</p>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder={t("Escribe una nota visible para tu equipo…")} className="mt-3 min-h-[80px] rounded-xl" data-testid="candidate-note-input" />
      <button onClick={add} disabled={busy || !text.trim()} className="mt-2 flex h-10 items-center gap-2 rounded-xl bg-slate-950 px-4 text-xs font-bold text-white disabled:opacity-50" data-testid="candidate-note-add">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {t("Añadir nota")}</button>
      {items === null ? <Loader2 className="mt-4 h-4 w-4 animate-spin text-slate-400" /> : (
        <ul className="mt-4 space-y-2">
          {items.map((n) => <NoteItem key={n.id} n={n} onDelete={remove} />)}
          {items.length === 0 && <li className="text-sm text-slate-400" data-testid="candidate-notes-empty">{t("Todavía no hay notas.")}</li>}
        </ul>
      )}
    </div>
  );
};
